import { useCallback, useEffect, useRef } from "react"

import airbrake from "src/airbrake-notifier"

import { GeminiLiveSession } from "./helpers/geminiLiveSession"

import useAudioCaptureWorklet from "./useAudioCaptureWorklet"
import useChimeMeetingAudioStream from "./useChimeMeetingAudioStream"
import useMeetingAudioGraph from "./useMeetingAudioGraph"

const toggleRoomAudio = (muted: boolean) => {
  const roomAudio = document.querySelector(
    "#room-audio"
  ) as HTMLAudioElement | null
  if (roomAudio) roomAudio.muted = muted
}

// Streams PCM (audio) from Chime's mixed meeting `MediaStream` into Gemini Live and plays translated audio.
const useGeminiLiveTranslation = ({
  audioVideo,
  targetLanguageCode,
  chimeMeetingId,
}) => {
  const chimeAudioStream = useChimeMeetingAudioStream(audioVideo)

  const geminiSessionRef = useRef<GeminiLiveSession | null>(null)

  const { setupGraph, teardownGraph } = useMeetingAudioGraph()
  const { setupCaptureWorklet, teardownCaptureWorklet } =
    useAudioCaptureWorklet(geminiSessionRef)

  const teardownAll = useCallback(() => {
    toggleRoomAudio(false) // restore room audio
    teardownCaptureWorklet() // disconnect capture node from audio graph
    teardownGraph() // closes the shared AudioContext now that all nodes have been removed

    geminiSessionRef.current?.close()
    geminiSessionRef.current = null
  }, [teardownCaptureWorklet, teardownGraph])

  useEffect(() => {
    if (!targetLanguageCode) return // feature not enabled
    if (!chimeMeetingId || !chimeAudioStream) return // chime meeting not ready

    let canceled = false

    const startTranslation = async () => {
      const geminiSession = await GeminiLiveSession.createAndConnect({
        chimeMeetingId,
        targetLanguageCode,
        onFatalClose: teardownAll,
      })
      if (canceled) {
        geminiSession.close()
        return
      }
      geminiSessionRef.current = geminiSession

      // build a single shared audio graph (AudioContext + source node) for the Chime stream
      const graph = await setupGraph(chimeAudioStream)
      if (canceled) {
        teardownAll()
        return
      }

      // attach the capture worklet to the graph — breaks audio into PCM chunks and sends them to Gemini
      await setupCaptureWorklet(graph, teardownAll)
      if (canceled) {
        teardownAll()
        return
      }

      toggleRoomAudio(true) // mute room volume so translation output can be heard clearly
    }

    startTranslation().catch(e => {
      if (canceled) return
      airbrake.notify({
        error: e,
        message: "Caught error in useGeminiLiveTranslation",
        params: {
          name: "useGeminiLiveTranslation",
          chimeMeetingId: chimeMeetingId,
        },
      })
      teardownAll()
    })

    return () => {
      canceled = true
      teardownAll()
    }
  }, [
    targetLanguageCode,
    chimeAudioStream,
    setupGraph,
    teardownGraph,
    setupCaptureWorklet,
    teardownCaptureWorklet,
    teardownAll,
    chimeMeetingId,
  ])
}

export default useGeminiLiveTranslation
